import { z } from "zod";
import { UpgradeBuildingSchema } from "./planet.schemas.js";
import { PlayerRaceSchema, ScienceIdSchema } from "./user.schemas.js";
import { ALL_SHIP_IDS, SHIP_GROUP_MAX_ITEMS, } from "../constants/ships.js";
/**
 * Zod mirrors for the tick-checkpoint event system. Stored planet docs are
 * not zod-validated today (there is no PlanetSchema), so only the pieces
 * that guard endpoint input — plus the enums/details worth reusing — live
 * here.
 */
export const TickEventTypeSchema = z.enum([
    "buildingBuild",
    "scienceResearch",
    "shipUpgrade",
    "shipBuild",
    "scrapRecycle",
]);
export const ResourceCostSchema = z.object({
    metal: z.number().optional(),
    minerals: z.number().optional(),
    gas: z.number().optional(),
    energy: z.number().optional(),
    ozone: z.number().optional(),
});
export const BuildingBuildDetailsSchema = z.object({
    buildingType: UpgradeBuildingSchema.shape.buildingType,
    targetLevel: z.number().int().positive(),
    zone: z.enum(["resource", "industry", "military"]),
    paidCost: ResourceCostSchema,
    durationSeconds: z.number().positive(),
    startDate: z.date(),
    /**
     * Plot this construct claims while pending (DS-28) — only set for a
     * level 0→1 construct, so the plot is reserved for it and cannot also
     * be handed to a second queued construct until this one resolves or is
     * cancelled.
     */
    plotId: z.string().optional(),
});
export const ShipUpgradeDetailsSchema = z.object({
    shipType: z.number().int().min(1).max(13),
    targetLevel: z.number().int().positive(),
    planetId: z.string(),
    paidCost: ResourceCostSchema,
    durationSeconds: z.number().positive(),
    startDate: z.date(),
    queueLane: PlayerRaceSchema.optional(),
    queuedByUserId: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------
/** Queue a building upgrade — same body the instant-upgrade endpoint used. */
export const QueueBuildingSchema = UpgradeBuildingSchema;
/**
 * Queue as many consecutive levels of one building as fit (Platinum "Queue
 * max", issue #219). The server decides the count; `maxLevels` is the count
 * the player was shown, so it never queues — or charges for — more than that.
 */
export const QueueMaxBuildingLevelsSchema = z.object({
    planetId: z.string(),
    buildingType: UpgradeBuildingSchema.shape.buildingType,
    plotId: z.string().optional(),
    maxLevels: z.number().int().positive().optional(),
});
/** Destroy one level of a building — same body shape as queueing an upgrade. */
export const DestroyBuildingSchema = UpgradeBuildingSchema;
/** Cancel a pending buildingBuild event by its per-planet eventId. */
export const CancelBuildingEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/**
 * Queue a science research — `planetId` is the paying planet (its resources
 * are debited); the resulting level applies to the player globally.
 */
export const QueueScienceSchema = z.object({
    planetId: z.string(),
    scienceId: ScienceIdSchema,
});
/**
 * Cancel a pending scienceResearch event by its profile-level eventId.
 * `planetId` must match the planet that originally paid (validated
 * server-side against the resolved event's `eventDetails.planetId`) so the
 * refund lands back on the planet the caller expects.
 */
export const CancelScienceEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/**
 * A `ShipId` in key position — JSON object keys are always strings, so the
 * numeric id arrives as `"3"`. Rejecting unknown ids here keeps them from
 * reaching `getShipDefinition`, which throws a plain `Error` (mapped to a
 * 500) rather than a `QueueError`.
 */
const ShipIdKeySchema = z
    .string()
    .refine((key) => ALL_SHIP_IDS.some((id) => String(id) === key), "Unknown ship id");
/**
 * Queue a batch of ships at the planet's shipyard. The quantity ceiling is a
 * transport-level sanity bound — the real limit is free population capacity,
 * enforced by the engine.
 */
export const BuildShipsSchema = z.object({
    planetId: z.string(),
    shipId: z.number().int().min(1).max(13),
    quantity: z.number().int().min(1).max(100000),
});
/** Cancel a pending shipBuild event by its per-planet eventId. */
export const CancelShipBuildEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/**
 * Robots-only (issue #91): queue a batch of ship-build orders as one linked
 * group whose combined delivery is held automatically for
 * `AUTO_DELIVERY_HOLD_HOURS` after the group's construction completes,
 * instead of docking automatically at each item's own `endDate`. `items`
 * mirrors {@link StartRecycleSchema}'s shape (keyed by ShipId, as a string).
 * The item ceiling is duplicated here purely as a transport-level bound; the
 * 2-item minimum and the Robots-only race gate stay with the engine and the
 * Cloud Function, which own the game rules.
 */
export const BuildShipGroupSchema = z.object({
    planetId: z.string(),
    items: z
        .record(ShipIdKeySchema, z.number().int().min(1).max(100000))
        // The engine enforces the same ceiling, but bounding it here keeps an
        // arbitrarily large record from reaching the transaction at all.
        .refine((items) => Object.keys(items).length <= SHIP_GROUP_MAX_ITEMS, `A ship build group may contain at most ${SHIP_GROUP_MAX_ITEMS} distinct ship types`),
});
/**
 * Deliver a Robots ship-build group on demand, before its automatic
 * `AUTO_DELIVERY_HOLD_HOURS` deadline — `groupId` is the shared id every
 * batch in the group carries (`ShipBuildDetails.groupId`).
 */
export const DeliverShipGroupSchema = z.object({
    planetId: z.string(),
    groupId: z.number().int().positive(),
});
/**
 * Queue a ship upgrade — `planetId` is the paying planet (must have Tech Center
 * / Stargate / Space Channel at level 1+); the resulting level applies to
 * the player globally, same shape as {@link QueueScienceSchema}.
 */
export const QueueShipUpgradeSchema = z.object({
    planetId: z.string(),
    shipId: z.number().int().min(1).max(13),
});
/**
 * Cancel a pending shipUpgrade event by its profile-level eventId.
 * `planetId` must match the planet that originally paid (validated
 * server-side against the resolved event's `eventDetails.planetId`), same
 * pattern as {@link CancelScienceEventSchema}.
 */
export const CancelShipUpgradeEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/**
 * Scrap a batch of docked ships for a partial refund — the overpopulation
 * lock's resolution action (issue #26). `ships` is keyed by ShipId (as a
 * string, since JSON object keys are always strings); quantities are
 * validated against what's actually docked server-side, not here.
 */
export const ScrapShipsSchema = z.object({
    planetId: z.string(),
    ships: z.record(z.string(), z.number().int().positive()),
});
/**
 * Queue a scrap-recycle batch at the Recovery Center — `ships` is the
 * requested ship selection (keyed by ShipId, as a string), converted into
 * a scrap cost server-side via `recycleScrapRequired` (issue #21).
 */
export const StartRecycleSchema = z.object({
    planetId: z.string(),
    ships: z.record(z.string(), z.number().int().positive()),
});
/** Cancel a pending scrapRecycle batch by its per-planet eventId. */
export const CancelRecycleEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/**
 * Deliver a pending scrapRecycle batch on demand, before its automatic
 * `AUTO_DELIVERY_HOLD_HOURS` deadline, by its per-planet eventId.
 */
export const DeliverRecycleEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Queue rush — pay Dark Matter to instantly complete a pending queue item.
// Same `{ planetId, eventId }` shape as the matching Cancel*EventSchema
// above; the server recomputes the price from the resolved event's own
// remaining time (`calculateRushCost`) and never trusts a client-supplied
// amount.
// ---------------------------------------------------------------------------
/** Instantly complete a pending buildingBuild event by its per-planet eventId. */
export const RushBuildingEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/** Instantly complete a pending shipBuild batch by its per-planet eventId (not available for a Robots delivery group). */
export const RushShipBuildEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/** Instantly complete a pending scrapRecycle batch by its per-planet eventId. */
export const RushRecycleEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/** Instantly complete a pending scienceResearch event by its profile-level eventId. */
export const RushScienceEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
/** Instantly complete a pending shipUpgrade event by its profile-level eventId. */
export const RushShipUpgradeEventSchema = z.object({
    planetId: z.string(),
    eventId: z.number().int().positive(),
});
//# sourceMappingURL=tickEvent.schemas.js.map